import { useQuery } from '@tanstack/react-query';
import { unwrap } from './api';
import { useInvalidating } from './query';

/** The response union every ts-rest client route returns. */
type ApiResponse = { status: number; body: unknown };

/**
 * The four shapes every resource in this app repeats: list it, add one, save
 * one, delete one. Each wraps `unwrap` at the status the contract promises, so
 * a route whose success code is anything else does not belong here.
 *
 * Body and DTO types are inferred from the route function handed in — passing
 * `api.finances.createWallet` is what makes `mutate` accept a wallet body and
 * nothing else. Nothing needs restating, so nothing can drift.
 *
 * The message is passed whole rather than templated from a noun: the strings
 * differ in wording ("add the wallet", "save the goal", "create the user")
 * and they are what users read. A failure is toasted with it; `success`, where
 * given, is toasted when the call lands.
 */

/** GET → 200. */
export function useResourceQuery<R extends ApiResponse>(
  queryKey: readonly unknown[],
  request: () => Promise<R>,
  fallback: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey,
    enabled: options?.enabled,
    queryFn: async () => unwrap(await request(), 200, fallback),
  });
}

/** POST → 201, then invalidate `queryKey`. */
export function useCreateResource<B, R extends ApiResponse>(
  queryKey: readonly unknown[],
  request: (args: { body: B }) => Promise<R>,
  fallback: string,
  success?: string,
) {
  return useInvalidating(
    queryKey,
    async (body: B) => unwrap(await request({ body }), 201, fallback),
    success,
  );
}

/**
 * PATCH/POST on `/:id` → 200. Takes the body with `id` mixed in, because that
 * is how the row components already call it.
 */
export function useUpdateResource<B, R extends ApiResponse>(
  queryKey: readonly unknown[],
  request: (args: { params: { id: string }; body: B }) => Promise<R>,
  fallback: string,
  success?: string,
) {
  return useInvalidating(
    queryKey,
    async ({ id, ...rest }: B & { id: string }) =>
      unwrap(
        // `Omit<B & { id }, 'id'>` is `B` for every body in this contract, but
        // TS cannot prove it in general. Sound while no request body has an
        // `id` field of its own — one that did would be dropped silently.
        await request({ params: { id }, body: rest as unknown as B }),
        200,
        fallback,
      ),
    success,
  );
}

/** DELETE → 204, then invalidate `queryKey`. */
export function useDeleteResource<R extends ApiResponse>(
  queryKey: readonly unknown[],
  request: (args: { params: { id: string } }) => Promise<R>,
  fallback: string,
) {
  return useInvalidating(queryKey, async (id: string) =>
    unwrap(await request({ params: { id } }), 204, fallback),
  );
}
